import { useState } from "react";
import { useSelector } from "react-redux";
import { Target, ExternalLink, Copy, AlertCircle, CheckCircle, HelpCircle } from "lucide-react";
import styles from "./ButtonSelector.module.css";
import { SelectUserWebsiteURL } from '../../../../DesignEditor/designEditorSlice';

export default function ButtonSelector({
    selectedButton,
    onButtonSelect,
    storeUrl = '', // Replace useShopInfo with prop
    cf_username = '' // Replace useShopInfo with prop
}) {
    const [buttonName, setButtonName] = useState(selectedButton?.name || '');
    const [cssSelector, setCssSelector] = useState(selectedButton?.selector || '');
    const [pageUrl, setPageUrl] = useState(selectedButton?.pageUrl || '');
    const [errors, setErrors] = useState({});
    const [showHelperModal, setShowHelperModal] = useState(false);
    const [testId] = useState(() => `test_${Date.now()}`);
    storeUrl = useSelector(SelectUserWebsiteURL);

    const validateSelector = (selector) => {
        if (!selector.trim()) {
            return 'CSS selector is required';
        }

        // Basic CSS selector validation
        try {
            document.querySelector(selector);
            return null; // No error
        } catch (error) {
            return 'Invalid CSS selector format';
        }
    };

    const validatePageUrl = (url) => {
        if (!url.trim()) {
            return 'Page URL is required';
        }

        try {
            new URL(url);
            return null; // No error
        } catch (error) {
            return 'Invalid URL format';
        }
    };

    const handleNameChange = (value) => {
        setButtonName(value);
        // Clear name error when user starts typing
        if (errors.name && value.trim()) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.name;
                return newErrors;
            });
        }
    };

    const handleSelectorChange = (value) => {
        setCssSelector(value);

        // Clear and re-validate selector
        if (value.trim()) {
            const error = validateSelector(value);
            setErrors(prev => ({
                ...prev,
                selector: error
            }));
        } else {
            // Clear error when field is empty (will be caught by hasValidInput)
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.selector;
                return newErrors;
            });
        }
    };

    const handlePageUrlChange = (value) => {
        setPageUrl(value);

        // Clear and re-validate URL
        if (value.trim()) {
            const error = validatePageUrl(value);
            setErrors(prev => ({
                ...prev,
                pageUrl: error
            }));
        } else {
            // Clear error when field is empty
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.pageUrl;
                return newErrors;
            });
        }
    };

    const hasValidInput = () => {
        const nameValid = buttonName.trim().length > 0;
        const selectorValid = cssSelector.trim().length > 0 && !errors.selector;
        const urlValid = pageUrl.trim().length > 0 && !errors.pageUrl;
        return nameValid && selectorValid && urlValid;
    };

    const handleSubmit = () => {
        const newErrors = {};

        if (!buttonName.trim()) {
            newErrors.name = 'Button name is required';
        }

        const selectorError = validateSelector(cssSelector);
        if (selectorError) {
            newErrors.selector = selectorError;
        }

        const urlError = validatePageUrl(pageUrl);
        if (urlError) {
            newErrors.pageUrl = urlError;
        }

        setErrors(newErrors);

        if (Object.keys(newErrors).length === 0) {
            const buttonConfig = {
                name: buttonName.trim(),
                selector: cssSelector.trim(),
                pageUrl: pageUrl.trim(),
                type: 'custom',
                detectionMethod: 'manual'
            };

            onButtonSelect(buttonConfig);
        }
    };

    const openSelectorTool = () => {
        setShowHelperModal(true);

        // Use specific page URL if provided, otherwise fall back to store URL
        const targetUrl = pageUrl.trim() || storeUrl;

        // Generate preview URL with query parameters
        const previewParams = new URLSearchParams({
            cf_element_selection: 'true',
            test_id: testId,
            mode: 'button_selector',
            return_url: window.location.href
        });

        // Parse the target URL and add query params
        const url = new URL(targetUrl);
        previewParams.forEach((value, key) => {
            url.searchParams.set(key, value);
        });

        const previewUrl = url.toString();

        // Open in new tab
        window.open(previewUrl, '_blank', 'noopener,noreferrer');

        // Start listening for selection results
        startListeningForSelection();
    };

    const startListeningForSelection = () => {
        const handleSelection = (event) => {
            if (event.data?.type === 'CF_ELEMENT_SELECTED' && event.data?.mode === 'button_selector') {
                const { selector, elementText } = event.data;

                // Auto-fill the form
                setButtonName(elementText || 'Selected Button');
                setCssSelector(selector);
                setShowHelperModal(false);

                // Clear any errors
                setErrors({});

                // Remove event listener
                window.removeEventListener('message', handleSelection);
            }
        };

        window.addEventListener('message', handleSelection);

        // Cleanup after 10 minutes
        setTimeout(() => {
            window.removeEventListener('message', handleSelection);
        }, 600000);
    };

    const copyQueryParams = () => {
        const params = `?cf_element_selection=true&test_id=${testId}&mode=button_selector`;
        navigator.clipboard.writeText(params);
        showToast('✅ Query parameters copied to clipboard!');
    };

    const showToast = (message) => {
        const toast = document.createElement('div');
        toast.textContent = message;
        toast.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            background: #10b981;
            color: white;
            padding: 12px 16px;
            border-radius: 6px;
            font-size: 14px;
            z-index: 10000;
        `;

        document.body.appendChild(toast);

        setTimeout(() => {
            document.body.removeChild(toast);
        }, 3000);
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h4 className={styles.title}>Button Configuration</h4>
                <p className={styles.description}>
                    Configure the button you want to test by providing a name and CSS selector.
                </p>
            </div>

            <div className={styles.form}>
                {/* Button Name Field */}
                <div className={styles.inputGroup}>
                    <label className={styles.label}>
                        Button Name <span className={styles.required}>*</span>
                    </label>
                    <input
                        type="text"
                        value={buttonName}
                        onChange={(e) => handleNameChange(e.target.value)}
                        placeholder="e.g., Add to Cart Button"
                        className={`${styles.input} ${errors.name ? styles.error : ''}`}
                    />
                    {errors.name && (
                        <div className={styles.errorMessage}>
                            <AlertCircle size={14} />
                            <span>{errors.name}</span>
                        </div>
                    )}
                    <div className={styles.inputHelp}>
                        Give your button a descriptive name for easy identification
                    </div>
                </div>

                {/* Page URL Field */}
                <div className={styles.inputGroup}>
                    <label className={styles.label}>
                        Page URL <span className={styles.required}>*</span>
                    </label>
                    <input
                        type="url"
                        value={pageUrl}
                        onChange={(e) => handlePageUrlChange(e.target.value)}
                        placeholder={`e.g., ${storeUrl}`}
                        className={`${styles.input} ${errors.pageUrl ? styles.error : ''}`}
                    />
                    {errors.pageUrl && (
                        <div className={styles.errorMessage}>
                            <AlertCircle size={14} />
                            <span>{errors.pageUrl}</span>
                        </div>
                    )}
                    <div className={styles.inputHelp}>
                        URL of the page where your button is located (used for previews and selection)
                    </div>
                </div>

                {/* CSS Selector Field */}
                <div className={styles.inputGroup}>
                    <label className={styles.label}>
                        CSS Selector <span className={styles.required}>*</span>
                    </label>
                    <div className={styles.selectorInputWrapper}>
                        <input
                            type="text"
                            value={cssSelector}
                            onChange={(e) => handleSelectorChange(e.target.value)}
                            placeholder="e.g., button[name='add'] or .product-form .btn"
                            className={`${styles.input} ${styles.selectorInput} ${errors.selector ? styles.error : ''}`}
                        />
                        <button
                            type="button"
                            onClick={openSelectorTool}
                            className={styles.helperButton}
                            title="Open selector finder tool"
                        >
                            <Target size={16} />
                            Find Selector
                        </button>
                    </div>
                    {errors.selector && (
                        <div className={styles.errorMessage}>
                            <AlertCircle size={14} />
                            <span>{errors.selector}</span>
                        </div>
                    )}
                    <div className={styles.inputHelp}>
                        CSS selector that targets your button element
                        <button
                            type="button"
                            className={styles.helpLink}
                            onClick={() => setShowHelperModal(true)}
                        >
                            <HelpCircle size={12} />
                            Need help?
                        </button>
                    </div>
                </div>

                {/* Submit Button */}
                <div className={styles.actions}>
                    <button
                        onClick={handleSubmit}
                        disabled={!hasValidInput()}
                        className={styles.submitButton}
                    >
                        <CheckCircle size={16} />
                        Use This Button
                    </button>
                </div>
            </div>

            {/* Helper Modal */}
            {showHelperModal && (
                <div className={styles.modal}>
                    <div className={styles.modalOverlay} onClick={() => setShowHelperModal(false)} />
                    <div className={styles.modalContent}>
                        <div className={styles.modalHeader}>
                            <h3>🔍 Selector Finder Tool</h3>
                            <button
                                onClick={() => setShowHelperModal(false)}
                                className={styles.closeButton}
                            >
                                ×
                            </button>
                        </div>

                        <div className={styles.modalBody}>
                            <div className={styles.step}>
                                <div className={styles.stepNumber}>1</div>
                                <div className={styles.stepContent}>
                                    <h4>Preview window opened</h4>
                                    <p>A new tab opened with your store. If it didn't open, check your popup blocker.</p>
                                </div>
                            </div>

                            <div className={styles.step}>
                                <div className={styles.stepNumber}>2</div>
                                <div className={styles.stepContent}>
                                    <h4>Visit the right page</h4>
                                    <p>Navigate to the page where your button is located. Add these query parameters to any URL:</p>

                                    <div className={styles.copyBox}>
                                        <code>?cf_element_selection=true&test_id={testId}&mode=button_selector</code>
                                        <button onClick={copyQueryParams} className={styles.copyButton}>
                                            <Copy size={14} />
                                            Copy
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className={styles.step}>
                                <div className={styles.stepNumber}>3</div>
                                <div className={styles.stepContent}>
                                    <h4>Click your button</h4>
                                    <p>Click on the button you want to test. The selector will be automatically copied and filled in this form.</p>
                                </div>
                            </div>
                        </div>

                        <div className={styles.modalActions}>
                            <button
                                onClick={() => setShowHelperModal(false)}
                                className={styles.gotItButton}
                            >
                                Got it!
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Success State */}
            {selectedButton && (
                <div className={styles.successCard}>
                    <CheckCircle size={20} className={styles.successIcon} />
                    <div className={styles.successContent}>
                        <h5>Button Configured: {selectedButton.name}</h5>
                        <p>Selector: <code>{selectedButton.selector}</code></p>
                    </div>
                </div>
            )}
        </div>
    );
}